import { reloadPreview, type WebContainer } from '@webcontainer/api';
import { atom } from 'nanostores';
import { chatStore } from './chat';
import type { FilesStore } from './files';
import { projectStore } from './project';
import { userStore } from './user';
import { workbenchStore, type TransferrableInspectedElement } from './workbench';
import { clientFetch } from '~/lib/.client/fetch';
import injectBadge from '~/lib/badge?raw';
import { SHOW_MADE_IN_BOLT_BADGE } from '~/lib/constants';
import { errorHandler } from '~/lib/error-handling';
import { PreviewTunnel } from '~/lib/preview';
import previewScript from '~/lib/preview-scripts?raw';
import { isInspectedImage } from '~/utils/inspect-element';
import { examplePrompts } from '~/utils/landingPage';
import { asyncValues, watchAll } from '~/utils/nanostore';
import { wait } from '~/utils/promises';

const WAITING_FRAMEWORKS = ['expo'];

export interface PreviewInfo {
  port: number;
  ready: boolean;
  baseUrl: string;
}

export class PreviewsStore {
  #availablePreviews = new Map<number, PreviewInfo>();
  #webcontainer: Promise<WebContainer>;
  #filesStore: FilesStore;
  #previewTunnel?: PreviewTunnel;

  activePreviewIndex = atom<number>(0);
  previewUrl = atom<string | undefined>(undefined);
  isReady = atom<boolean>(false);
  tunnelPassword = atom<string | undefined>(undefined);
  hasSelectedPreview = false;
  ref: HTMLIFrameElement | null = null;

  previews = atom<PreviewInfo[]>([]);

  constructor(webcontainerPromise: Promise<WebContainer>, filesStore: FilesStore) {
    this.#webcontainer = webcontainerPromise;
    this.#filesStore = filesStore;
    this.#init();
  }

  setActivePreviewIndex(index: number) {
    this.activePreviewIndex.set(index);
  }

  async getTunnelURL() {
    if (!this.#previewTunnel) {
      // this happens if the user does not have the preview tunneling feature flag enabled
      return undefined;
    }

    const activePreview = this.previews.get()[this.activePreviewIndex.get()];

    if (!activePreview) {
      // if we don't have an active preview, we don't know which port to generate the URL for
      return undefined;
    }

    return this.#previewTunnel.getURL(activePreview.port);
  }

  getTunnelPassword() {
    return this.#previewTunnel?.getPassword();
  }

  async #init() {
    const webcontainer = await this.#webcontainer;

    const user = userStore.getUser();

    if (user?.featureFlags?.previewTunneling) {
      // only create the tunnel if the user has the preview tunneling feature flag enabled
      this.#previewTunnel = new PreviewTunnel(webcontainer);
    }

    webcontainer.on('port', (port, type, url) => {
      let previewInfo = this.#availablePreviews.get(port);

      if (type === 'close' && previewInfo) {
        this.#availablePreviews.delete(port);
        this.previews.set(this.previews.get().filter((preview) => preview.port !== port));

        if (this.#availablePreviews.size === 0) {
          // close the tunnel if no previews are available anymore
          this.#previewTunnel?.close();
        }

        return;
      }

      const previews = this.previews.get();

      if (!previewInfo) {
        previewInfo = { port, ready: type === 'open', baseUrl: url };
        this.#availablePreviews.set(port, previewInfo);
        previews.push(previewInfo);

        if (previews.length > 1 && !this.hasSelectedPreview) {
          const minPortIndex = this.#findMinPortIndex(previews);
          this.activePreviewIndex.set(minPortIndex);
          this.hasSelectedPreview = true;
        }

        const projectId = projectStore.getProjectId();

        const id = user?.id && projectId ? `${user.id}:${projectId}` : crypto.randomUUID();

        this.#previewTunnel?.open(id)?.then(async () => {
          const url = await this.getTunnelURL();

          if (!url) {
            // if we don't have a URL, we just fallback to a url without session ID
            this.previewUrl.set('https://(...)preview.bolt.host');
          } else {
            const parsedURL = new URL(url);

            const [sessionId, ...rest] = parsedURL.hostname.split('.');

            this.previewUrl.set(
              `${parsedURL.protocol}//${sessionId.slice(0, 7)}(...)${rest.join('.')}${parsedURL.pathname}`,
            );

            this.tunnelPassword.set(this.getTunnelPassword());
          }
        });
      }

      previewInfo.ready = type === 'open';
      previewInfo.baseUrl = url;

      this.previews.set([...previews]);
    });

    let prevActivePrevew: PreviewInfo | null = null;

    watchAll(this.#filesStore.framework, this.activePreviewIndex, this.previews).subscribe(
      ([framework, activePreviewIndex, previews]) => {
        const activePreview = previews[activePreviewIndex];

        if (prevActivePrevew === activePreview) {
          return;
        }

        prevActivePrevew = activePreview;

        if (WAITING_FRAMEWORKS.includes(framework?.name ?? '')) {
          this.isReady.set(false);
          return;
        }

        this.isReady.set(true);
      },
    );

    webcontainer.internal.previewScript.on('message', (data) => {
      const typedData = data as { type: string; value: unknown };

      if (typedData.type === 'init') {
        errorHandler.clear((problem) => problem.type === 'preview' || problem.type === 'supabase-function');
      }

      if (typedData.type === 'badge-click') {
        window.open('https://bolt.new/pricing', '_blank');
      }

      if (typedData.type === 'framework-ready') {
        this.isReady.set(true);
      }

      if (typedData.type === 'inspect') {
        const previousAttachment = chatStore.attachments
          .get()
          .find((attachment) => isInspectedImage(attachment.file.name));

        if (previousAttachment) {
          chatStore.removeAttachment(previousAttachment);
        }

        const inspectedElement = typedData.value as TransferrableInspectedElement;

        const buffer = inspectedElement.buffer;

        const file = new File([buffer], `${inspectedElement.tagName}.inspect-element-${Date.now()}.jpeg`, {
          type: 'image/jpeg',
        });

        workbenchStore.setInspectedElement({
          tagName: inspectedElement.tagName,
          html: inspectedElement.html,
          file,
          pathname: inspectedElement.pathname,
        });
      }
    });

    this.#setPreviewScript(webcontainer);

    while (true) {
      await wait(6000);

      webcontainer.internal.previewScript.broadcast({ type: 'heartbeat' });

      const result = await Promise.race([
        wait(2000).then(() => 'timeout'),
        new Promise<'heartbeat'>((resolve) => {
          const unsubscribe = webcontainer.internal.previewScript.on('message', (event) => {
            const data = event as { type: 'heartbeat' };

            if (data.type === 'heartbeat') {
              unsubscribe();
              resolve(data.type);
            }
          });
        }),
      ]);

      if (result === 'timeout' && this.ref) {
        await reloadPreview(this.ref);
      }
    }
  }

  async #setPreviewScript(webcontainer: WebContainer) {
    webcontainer.setPreviewScript(previewScript);

    if (!SHOW_MADE_IN_BOLT_BADGE) {
      return;
    }

    let badge = false;

    const projectSlugs = asyncValues(projectStore.getSlugStore());

    for await (const slug of projectSlugs) {
      if (slug == null) {
        continue;
      }

      let needsBadge = false;

      const isTemplate = examplePrompts.some((template) => slug === template.slug);

      if (!isTemplate) {
        try {
          const response = await clientFetch(`/api/projects/${slug}/badge`);

          needsBadge = await response.json();
        } catch {
          // ignore
        }
      }

      if (needsBadge !== badge) {
        badge = needsBadge;
        webcontainer.setPreviewScript(badge ? `${injectBadge}\n${previewScript}` : previewScript);
      }
    }
  }

  #findMinPortIndex(previews: PreviewInfo[]) {
    return previews.reduce(
      (minIndex, preview, index, array) => (preview.port < array[minIndex].port ? index : minIndex),
      0,
    );
  }
}
